import { buildMeasurement } from 'core/service/measurements/measurementUtils';
import { BootstrapConfig } from 'core/type/BootstrapConfig.types';
import { logInfo, logWarn } from 'core-framework/util/LogMessage';
import { MeasurementName } from 'core/constant/MeasurementName';
import { sendMeasurement } from 'core/messaging/measurement/measurement';
import { ActivityGqlOperation, GqlApiError } from 'store/api/api.types';
import { IMeasurementTags } from '@agoda/messaging-client';
import { ActivitiesPageTypes } from 'core/constant/analytics/AnalyticsPageType';
import { MeasurementStatus } from 'core/constant/MeasurementStatus';
import { toSafeString } from 'store/api/apiUtils';
import { SearchErrorCode } from 'gql/autogen/SearchErrorCode';
import { SearchSubErrorCode } from 'gql/autogen/SearchSubErrorCode';

const sendSearchGqlMeasurements = (
    message: string,
    measurementName: MeasurementName,
    pageTypeId: ActivitiesPageTypes,
    config: BootstrapConfig,
    tags: IMeasurementTags,
): boolean => {
    const isSuccess = measurementName === MeasurementName.GqlSearchSuccess || measurementName === MeasurementName.GqlSearchAapiNoResult;
    const measurement = buildMeasurement(ActivityGqlOperation.search, measurementName, pageTypeId, message, 1, false, config);

    measurement.tags = {
        ...measurement.tags,
        ...tags,
    };
    measurement.tags.status = isSuccess ? MeasurementStatus.Success : MeasurementStatus.Failed;

    sendMeasurement({ ...measurement, name: MeasurementName.GqlSearchStatus }, pageTypeId);

    if (isSuccess) {
        logInfo(message, pageTypeId, measurement.tags);
        return true;
    }
    logWarn(message, pageTypeId, measurement.tags);
    return true;
};

/**
 * Must be called from 'React.useEffect' to avoid sending repeated measurements.
 */
export const measureSearchApiResponse = (isSuccess: boolean, numberOfResults: number, pageTypeId: ActivitiesPageTypes, config: BootstrapConfig, error?: GqlApiError): boolean => {
    const messageLowerCase = toSafeString(error?.message).toLowerCase();

    const measurementTags: IMeasurementTags = {
        statusCode: toSafeString(error?.status),
        subErrorCode: error?.subErrorCode || SearchSubErrorCode.EMPTY_SUB_ERROR_CODE,
        errorCode: error?.errorCode || SearchErrorCode.EMPTY_ERROR_CODE,
    };

    if (isSuccess) {
        if (numberOfResults === 0) {
            return sendSearchGqlMeasurements('zero results', MeasurementName.GqlSearchAapiNoResult, pageTypeId, config, measurementTags);
        }
        return sendSearchGqlMeasurements('success', MeasurementName.GqlSearchSuccess, pageTypeId, config, measurementTags);
    }

    return sendSearchGqlMeasurements(messageLowerCase, MeasurementName.GqlSearchError, pageTypeId, config, measurementTags);
};

export const measureLanguageNotSupported = (locale: string, pageTypeId: number): boolean => {
    const measurement = buildMeasurement('change_language', MeasurementName.LanguageNotSupported, pageTypeId);
    measurement.tags.locale = locale;

    sendMeasurement(measurement, pageTypeId);
    return true;
};
